<template>
  <div class="news-detail container py-5">
    <div v-if="loading" class="text-center py-5 small text-muted">Loading…</div>
    <div v-else-if="error" class="text-center py-5 text-danger">Failed to load news.</div>
    <div v-else-if="news">
      <header class="news-header d-flex align-items-center justify-content-center flex-wrap">
        <h1 class="news-main-title mb-0">{{ news.mainTitle }}</h1>
        <small class="news-date ms-3">{{ formatDate(news.strPublishDate) }}</small>
      </header>

      <div v-if="news.hasImage" class="text-center mt-4">
        <img :src="imageUrl" alt="news image" class="news-image" />
      </div>

      <div class="news-subtitle mt-4 text-center">
        {{ news.subTitle}}
      </div>

      <article class="news-content mt-4" v-html="news.contentHtml"></article>
    </div>
    <div v-else class="text-center py-5 small text-muted">No news found.</div>
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import apiClient from '@/apiClient';
import { MONTHS } from '@/constants';
import { TIPSTAG_ERROR } from '@/constants';

const { proxy } = getCurrentInstance();

const route = useRoute();

const news = ref(null);
const loading = ref(!news.value);
const error = ref(false);

// computed image url and existence check (preload to avoid broken icon)
const imageUrl = computed(() => (news.value ? `/news/${news.value.newsId}.png` : null));
const imageExists = ref(false);

watch(imageUrl, (url) => {
  imageExists.value = false;
  if (!url) return;
  const img = new Image();
  img.onload = () => { imageExists.value = true; };
  img.onerror = () => { imageExists.value = false; };
  img.src = url;
});

function formatDate(d) {
  if (!d) return '';
  const dt = new Date(d);
  // const month = MONTHS && MONTHS[dt.getMonth()] ? MONTHS[dt.getMonth()] : dt.toLocaleString(undefined, { month: 'short' });
  return `${MONTHS[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`;
}

onMounted(async () => {
  if (news.value) {
    loading.value = false;
    return;
  }

  const id = route.params.newsId;
  if (!id) {
    error.value = true;
    loading.value = false;
    return;
  }

  loading.value = true;
  error.value = false;
  try {
    const response = await apiClient.post('/Main/getNewsDetail', { newsId: id });
    if(response && response.data && response.data.newsDetail) {
      news.value = response.data.newsDetail;      
    }
    
  } catch (e) {
    if(e.response && e.response.data && e.response.status === 400) {    
     proxy.$handleTips(e.response.data.tag, e.response.data.msg, e);
    } else {
      proxy.$handleTips(TIPSTAG_ERROR, 'Network error occurred. Please try again later.', e);
    } 
    error.value = true;
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.news-detail { color: rgba(255,255,255,0.94); }

/* header: title centered, date to the right but visually next to title */
.news-header {
  gap: .75rem;
  text-align: center;
}
.news-main-title {
  font-size: 1.9rem;
  font-weight: 700;
  color: rgba(255,255,255,0.98);
}
.news-date {
  color: rgba(255,255,255,0.62);
  font-size: 0.95rem;
  white-space: nowrap;
}

/* image */
.news-image {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(2,6,23,0.6);
  display: inline-block;
}

/* subtitle */
.news-subtitle {
  color: rgba(255,255,255,0.72);
  font-size: 1.02rem;
  font-style: italic;
}

/* content */
.news-content {
  margin-top: 1.25rem;
  color: rgba(255,255,255,0.9);
  line-height: 1.8;
  font-size: 1rem;
}

/* responsive adjustments */
@media (max-width: 767.98px) {
  .news-main-title { font-size: 1.4rem; }
  .news-image { border-radius: 6px; }
  .news-content { font-size: 0.97rem; line-height: 1.6; }
}
</style>